import { CONTENT_TYPES, GENERATION_STATUS } from "constants/phase";
import { createSelector } from "reselect";

import { contentVariantEntityAdapter, contentVariantSlice } from "./slice";

const selectVariantState = (state) => state[contentVariantSlice.name];

export const selectContentVariantsIds = createSelector(selectVariantState, (variantState) =>
  contentVariantEntityAdapter.getSelectors().selectIds(variantState),
);

export const selectContentVariantEntites = createSelector(selectVariantState, (variantState) =>
  contentVariantEntityAdapter.getSelectors().selectEntities(variantState),
);

export const selectContentVariantTotalItems = createSelector(
  selectVariantState,
  (variantState) => variantState.totalItems,
);

export const selectContentVariantsDetails = createSelector(selectVariantState, (variantState) =>
  contentVariantEntityAdapter.getSelectors().selectAll(variantState),
);

export const selectContentVariantDetailsByClipId = createSelector(
  selectContentVariantsDetails,
  (_, props) => props?.clipId,
  (variants, clipId) => variants.filter((variant) => variant?.variant?.contentId === clipId),
);

export const selectContentVariantsWithStatusByClipId = createSelector(selectContentVariantDetailsByClipId, (variants) =>
  variants.filter(
    (variant) =>
      variant?.generation?.status === GENERATION_STATUS.COMPLETED ||
      variant?.generation?.status === GENERATION_STATUS.PROCESSING ||
      variant?.generation?.status === GENERATION_STATUS.INITIATED,
  ),
);

export const selectContentVariantProcessingContentVariantIds = createSelector(
  selectVariantState,
  (variantState) => variantState.processingContentVariantIds,
);

export const selectContentVariantsWithInitiatedOrProcessingStatus = createSelector(
  selectContentVariantsDetails,
  selectContentVariantProcessingContentVariantIds,
  (variants, processingContentVariantIds) =>
    variants.filter(
      (variant) =>
        (processingContentVariantIds.includes(variant?.variant?.id) &&
          variant?.generation?.status === GENERATION_STATUS.INITIATED) ||
        variant?.generation?.status === GENERATION_STATUS.PROCESSING,
    ),
);

export const selectContentVariantsWithCompletedStatus = createSelector(selectContentVariantsDetails, (variants) =>
  variants.filter((variant) => variant?.generation?.status === GENERATION_STATUS.COMPLETED),
);

export const selectContentVariantsWithCompletedStatusByContentId = createSelector(
  selectContentVariantsWithCompletedStatus,
  (_, props) => props?.contentId,
  (variants, contentId) => variants.filter((variant) => variant?.variant?.contentId === contentId),
);

export const selectContentVariantsWithCompletedStatusByContentIdAndAspectRatio = createSelector(
  selectContentVariantsWithCompletedStatusByContentId,
  (_, props) => props?.aspectRatio,
  (variants, aspectRatio) => variants.filter((variant) => variant?.variant?.aspectRatio === aspectRatio),
);

export const selectContentVariantsWithCompletedStatusCount = createSelector(
  selectContentVariantsWithCompletedStatus,
  (variants) => variants.length,
);

export const selectContentVariantsWithCompletedStatusCountByContentId = createSelector(
  selectContentVariantsWithCompletedStatusByContentId,
  (variants) => variants.length,
);

export const selectContentVariantsWithFailedOrErroredStatus = createSelector(selectContentVariantsDetails, (variants) =>
  variants.filter(
    (variant) =>
      variant?.generation?.status === GENERATION_STATUS.FAILED ||
      variant?.generation?.status === GENERATION_STATUS.ERRORED,
  ),
);

export const selectContentVariantsCount = createSelector(selectContentVariantsIds, (ids) => ids.length);

export const selectContentVariantById = createSelector(
  selectVariantState,
  (_, props) => {
    return {
      contentId: props.contentId,
      contentType: props.contentType,
    };
  },
  (variantState, { contentId, contentType }) => {
    if (contentType === CONTENT_TYPES.VARIANT) {
      return contentVariantEntityAdapter.getSelectors().selectById(variantState, contentId) || null;
    }

    return null;
  },
);

export const selectContentVariantVariantById = createSelector(
  selectContentVariantById,
  (_, props) => props.variantData,
  (contentVariant, variantData) => {
    return variantData || contentVariant?.variant;
  },
);

export const selectContentVariantSubtitlesById = createSelector(
  selectContentVariantById,
  (contentVariant) => contentVariant?.subtitles,
);

export const selectContentVariantGenerationById = createSelector(
  selectContentVariantById,
  (contentVariant) => contentVariant?.generation,
);

export const selectContentVariantByIdWithFirstSubtitle = createSelector(
  selectContentVariantVariantById,
  selectContentVariantSubtitlesById,
  (variant, subtitles) => {
    if (variant && subtitles) {
      return {
        variant,
        subtitle: subtitles?.[0],
      };
    }

    return null;
  },
);

export const selectContentVariantVariantStreamId = createSelector(
  selectContentVariantVariantById,
  (variant) => variant?.streamId || variant?.content?.streamId,
);

export const selectContentVariantVariantEntityId = createSelector(
  selectContentVariantVariantById,
  (variant) => variant?.entityId,
);

export const selectContentVariantVariantVideoUrl = createSelector(
  selectContentVariantVariantById,
  (variant) => variant?.url || variant?.videoUrl,
);

export const selectContentVariantVariantTitle = createSelector(
  selectContentVariantVariantById,
  (variant) => variant?.content?.episodeTitle,
);

export const selectContentVariantVariantContentId = createSelector(
  selectContentVariantVariantById,
  (variant) => variant?.contentId,
);

export const selectContentVariantFailedCount = createSelector(
  selectVariantState,
  (variantState) => variantState.failedCount,
);

export const selectContentVariantCompletedCount = createSelector(
  selectVariantState,
  (variantState) => variantState.completedCount,
);

export const selectContentVariantCurrentlyProcessingCount = createSelector(
  selectVariantState,
  selectContentVariantCompletedCount,
  selectContentVariantFailedCount,
  (variantState, completedCount, failedCount) =>
    variantState.processingContentVariantIds.length - completedCount - failedCount,
);
